import { useEffect, useState } from "react";
import api from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquareText, ShieldAlert, ShieldCheck } from "lucide-react";

export default function SmsLogsDialog() {
  const [logs, setLogs] = useState([]);
  const [provider, setProvider] = useState({ provider: "mock", configured: false });
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [logsRes, provRes] = await Promise.all([
      api.get("/sms-logs"),
      api.get("/sms-provider"),
    ]);
    setLogs(logsRes.data);
    setProvider(provRes.data);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const configured = provider.configured;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" data-testid="sms-logs-btn" title="SMS logs">
          <MessageSquareText className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>SMS Reminders</DialogTitle></DialogHeader>
        <div className={`rounded-lg border p-3 flex items-start gap-3 ${configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="sms-provider-status">
          {configured ? <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
          <div className="text-sm">
            {configured ? (
              <>
                <span className="font-medium text-emerald-700">MSG91 active</span>
                <p className="text-xs text-slate-600">Sending real SMS via MSG91 sender <span className="font-mono">{provider.sender_id || "TXTBIZ"}</span> (default country code {provider.default_country_code}).</p>
              </>
            ) : (
              <>
                <span className="font-medium text-amber-700">MOCKED — MSG91 not configured</span>
                <p className="text-xs text-slate-600">Messages are stored but NOT delivered. Add <span className="font-mono">MSG91_AUTH_KEY</span>, <span className="font-mono">MSG91_SENDER_ID</span> and your <span className="font-mono">MSG91_FLOW_ID_*</span> template ids to <span className="font-mono">backend/.env</span> and restart to enable real delivery.</p>
              </>
            )}
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto divide-y mt-2" data-testid="sms-logs-list">
          {logs.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No SMS reminders sent yet.</p>
          ) : logs.map((l) => (
            <div key={l.id} className="py-3 flex items-start gap-3">
              <Badge variant={l.kind === "booking_confirmation" ? "default" : "secondary"} className="mt-0.5 shrink-0">
                {l.kind === "booking_confirmation" ? "Booking" : "Reminder"}
              </Badge>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900">{l.message}</p>
                <p className="text-xs text-slate-400 mt-1">
                  To: {l.mobile} ·{" "}
                  <span className={l.status === "MOCKED" ? "text-amber-600" : l.status === "FAILED" ? "text-red-600" : "text-emerald-600"}>
                    {l.status}
                  </span>{" "}
                  · {new Date(l.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
